/**
 * Shared transport layer for the CMMD API client.
 *
 * All CMMD requests flow through `request<T>`, which handles:
 * - credentials: "include" always
 * - CMMD envelope unwrap ({ data, meta } → data)
 * - 401 → onAuthExpired callback + throw CmmdApiError { kind: "auth" }
 * - 403 → onAuthExpired callback (no redirect) + throw CmmdApiError { kind: "permission" }
 * - Retryable 429/5xx with exponential backoff (GET-only by default)
 * - 204 / empty body → undefined
 * - Non-2xx → parse error body → throw CmmdApiError { kind: "client" | "server" }
 */

// ── Error class ───────────────────────────────────────────────────────────────

export type CmmdApiErrorKind = "auth" | "permission" | "client" | "server" | "network";

export class CmmdApiError extends Error {
  readonly kind: CmmdApiErrorKind;
  readonly status: number | null;
  readonly code: string | null;
  readonly body: unknown;

  constructor(opts: {
    kind: CmmdApiErrorKind;
    message: string;
    status?: number | null;
    code?: string | null;
    body?: unknown;
  }) {
    super(opts.message);
    this.name = "CmmdApiError";
    this.kind = opts.kind;
    this.status = opts.status ?? null;
    this.code = opts.code ?? null;
    this.body = opts.body;
  }

  isAuth(): boolean {
    return this.kind === "auth";
  }

  static isAuth(err: unknown): err is CmmdApiError {
    return err instanceof CmmdApiError && err.kind === "auth";
  }
}

// ── Request options ───────────────────────────────────────────────────────────

export interface RequestOptions {
  readonly method: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  readonly path: string;
  readonly query?: Record<string, string | number | undefined>;
  readonly body?: unknown;
  readonly form?: FormData;
  readonly signal?: AbortSignal;
  /** Override: retry on non-GET requests. Default: only GET is retried. */
  readonly allowRetry?: boolean;
  /** Preserve a canonical `{ data, meta, pagination }` envelope when its metadata is required. */
  readonly unwrapEnvelope?: boolean;
  /** Do not attach the configured bearer token for cookie-authenticated same-origin helper calls. */
  readonly suppressBearerToken?: boolean;
}

export interface TransportConfig {
  readonly resolveUrl: (path: string) => string;
  /** Max retry attempts for retryable status codes. Default: 3. */
  readonly maxRetries?: number;
  /** Called when the server responds 401 or 403. Receives the HTTP status so handlers can distinguish session expiry (401) from authorization denial (403); handlers SHOULD early-return on 403 to avoid spurious logouts. */
  readonly onAuthExpired?: (status?: number) => void | Promise<void>;
  /** Bearer token to attach as an Authorization header (desktop cloud mode). */
  readonly bearerToken?: string;
  /** Lazy bearer resolver for singleton clients whose environment can change at runtime. */
  readonly resolveBearerToken?: () => string | null | undefined;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const RETRY_BASE_MS = 500;
const RETRYABLE_STATUS_CODES = new Set([429, 502, 503, 504]);

// ── Implementation ────────────────────────────────────────────────────────────

function buildUrl(fullUrl: string, query?: Record<string, string | number | undefined>): string {
  const url = new URL(fullUrl);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function parseErrorBody(
  response: Response,
): Promise<{ message: string; code: string | null; raw: unknown }> {
  let raw: unknown = null;
  let message = `HTTP ${response.status}`;
  let code: string | null = null;

  try {
    const text = await response.text();
    if (text) {
      try {
        raw = JSON.parse(text) as unknown;
        if (raw && typeof raw === "object") {
          const obj = raw as Record<string, unknown>;
          if (typeof obj["message"] === "string") message = obj["message"];
          else if (typeof obj["error"] === "string") message = obj["error"];
          if (typeof obj["code"] === "string") code = obj["code"];
        }
      } catch {
        message = text.slice(0, 200);
        raw = text;
      }
    }
  } catch {
    // response body unreadable
  }

  return { message, code, raw };
}

function unwrapEnvelope<T>(body: unknown): T {
  if (body && typeof body === "object" && "data" in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

export async function request<T>(opts: RequestOptions, cfg: TransportConfig): Promise<T> {
  const maxRetries = cfg.maxRetries ?? 3;
  const canRetry = opts.allowRetry ?? opts.method === "GET";

  const url = buildUrl(cfg.resolveUrl(opts.path), opts.query);

  const headers: Record<string, string> = { Accept: "application/json" };
  if (!opts.form) headers["Content-Type"] = "application/json";
  const bearerToken = opts.suppressBearerToken
    ? null
    : (cfg.bearerToken ?? cfg.resolveBearerToken?.());
  if (bearerToken) headers["Authorization"] = `Bearer ${bearerToken}`;

  const init: RequestInit = {
    method: opts.method,
    headers,
    credentials: "include",
    ...(opts.signal !== undefined ? { signal: opts.signal } : {}),
  };

  if (opts.form) {
    init.body = opts.form;
  } else if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }

  let attempt = 0;

  while (true) {
    let response: Response;

    try {
      response = await fetch(url, init);
    } catch (err) {
      throw new CmmdApiError({
        kind: "network",
        message: err instanceof Error ? err.message : "Network error",
        body: err,
      });
    }

    // Auth failure (session expired / invalid token)
    if (response.status === 401) {
      const { message, code } = await parseErrorBody(response);
      await cfg.onAuthExpired?.(response.status);
      throw new CmmdApiError({
        kind: "auth",
        message:
          message ||
          `CMMD authentication failed (${response.status}). Please sign out and sign back in.`,
        status: response.status,
        code,
      });
    }

    // Permission denied (valid session, insufficient privileges)
    if (response.status === 403) {
      const { message, code } = await parseErrorBody(response);
      await cfg.onAuthExpired?.(response.status);
      throw new CmmdApiError({
        kind: "permission",
        message: message || "You do not have permission to perform this action.",
        status: response.status,
        code,
      });
    }

    // Retryable errors
    if (RETRYABLE_STATUS_CODES.has(response.status) && canRetry && attempt < maxRetries - 1) {
      attempt++;
      await sleep(RETRY_BASE_MS * Math.pow(2, attempt - 1));
      continue;
    }

    if (RETRYABLE_STATUS_CODES.has(response.status) && (!canRetry || attempt >= maxRetries - 1)) {
      const { message, code, raw } = await parseErrorBody(response);
      throw new CmmdApiError({
        kind: "server",
        message: `CMMD server error (${response.status})${attempt > 0 ? ` after ${attempt + 1} attempts` : ""}: ${message}`,
        status: response.status,
        code,
        body: raw,
      });
    }

    // 204 or empty
    if (response.status === 204 || response.headers.get("content-length") === "0") {
      return undefined as T;
    }

    // Other non-2xx
    if (!response.ok) {
      const { message, code, raw } = await parseErrorBody(response);
      throw new CmmdApiError({
        kind: response.status >= 500 ? "server" : "client",
        message,
        status: response.status,
        code,
        body: raw,
      });
    }

    // 2xx with body
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      return undefined as T;
    }

    return opts.unwrapEnvelope === false ? (body as T) : unwrapEnvelope<T>(body);
  }
}
